import {
  validateIsRequired,
  validatePattern,
  type ValidationReturn,
} from 'formtastisch'
import { type IntlShape } from 'react-intl'

import {
  VALIDATION_ERROR_EMAIL_IS_INVALID,
  VALIDATION_ERROR_IS_REQUIRED,
  VALIDATION_ERROR_MIN_MAX_VALUE,
  VALIDATION_ERROR_MIN_VALUE,
} from './validationError'

const getEmailRegex = (): RegExp => /^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,63}$/iu

// eslint-disable-next-line prefer-named-capture-group -- the rule was disabled before a comment became required
const getPhoneRegExp = (): RegExp => /^(\+?[\d -]{8,})$/u

export const validateEmail = (
  value: string,
  message?: string,
): ValidationReturn => {
  return validatePattern(
    value,
    getEmailRegex(),
    message ?? VALIDATION_ERROR_EMAIL_IS_INVALID,
  )
}

export const validatePhoneNumber = (
  value: string,
  message?: string,
): ValidationReturn => {
  const trimmedPhoneNumber = value.replaceAll(/[ +]/gu, '')

  return validatePattern(trimmedPhoneNumber, getPhoneRegExp(), message?.trim())
}

export const validateRequired = (
  value?: string,
  message?: string,
): ValidationReturn => {
  return validateIsRequired(value, message ?? VALIDATION_ERROR_IS_REQUIRED)
}

export const validateNotEmpty = (
  value: string,
  message?: string,
): ValidationReturn => {
  if (!value || value.trim() === '') {
    return { errors: message, valid: false }
  }

  return { valid: true }
}

export const validateMinValue = (
  value: number,
  minValue: number,
  message?: string,
): ValidationReturn => {
  if (value >= minValue) {
    return { valid: true }
  }

  return { errors: message ?? VALIDATION_ERROR_MIN_VALUE, valid: false }
}

export const validateMinMaxValue = ({
  maxValue,
  message,
  minValue,
  value,
}: {
  maxValue: number
  message?: string
  minValue: number
  value: number
}): ValidationReturn => {
  if (value >= minValue && value <= maxValue) {
    return { valid: true }
  }

  return { errors: message ?? VALIDATION_ERROR_MIN_MAX_VALUE, valid: false }
}

export const validateMaxLength = (
  value: string,
  maxLength: number,
  message?: string,
): ValidationReturn => {
  if (typeof value === 'string' && value.length <= maxLength) {
    return { valid: true }
  }

  return { errors: message, valid: false }
}

export const validateArrayNotEmpty = (
  value: unknown[],
  message?: string,
): ValidationReturn => {
  if (value.length > 0 && !value.includes('')) {
    return { valid: true }
  }

  return { errors: message, valid: false }
}

export const validateTrue = (
  value?: boolean,
  message?: string,
): ValidationReturn => {
  if (value) {
    return { valid: true }
  }

  return { errors: message, valid: false }
}

export const getMinLengthErrorMessage = (
  intl: IntlShape,
  minLength: number,
): string =>
  intl.formatMessage(
    {
      id: 'form.error.input.minLength',
    },
    {
      minLength,
    },
  )

export const getMaxLengthErrorMessage = (
  intl: IntlShape,
  maxLength: number,
): string =>
  intl.formatMessage(
    {
      id: 'form.error.input.maxLength',
    },
    {
      maxLength,
    },
  )
